import { useState, useEffect, useRef } from 'react';
import { db, collection, getDocs, doc, getDoc,
  onSnapshot, query, orderBy, serverTimestamp } from '../firebase/config';
import { limit } from 'firebase/firestore';
import { toISO, addDays } from '../hooks';
import { auth } from '../firebase/config';
import ModuleLoader from './ModuleLoader';
import { useAppContext } from '../AppContext';

// ── helpers ───────────────────────────────────────────────────────────────────
const todayStr = () => toISO(new Date());
const fmtTime  = ts => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleTimeString('en-IN', { hour:'numeric', minute:'2-digit', hour12:true });
};

// Activity type config: icon, colour, label formatter
const ACTIVITY_CONFIG = {
  order_new:         { icon:'ti-clipboard-list', color:'#4318FF', bg:'rgba(67,24,255,0.1)',  label: a => `New order — ${a.meta?.customerName||''}` },
  order_delivered:   { icon:'ti-truck',          color:'#01B574', bg:'rgba(1,181,116,0.1)',  label: a => `Delivered — ${a.meta?.customerName||''}` },
  order_deleted:     { icon:'ti-trash',          color:'#E53E3E', bg:'rgba(229,62,62,0.1)',  label: a => `Order deleted — ${a.meta?.customerName||''}` },
  production_saved:  { icon:'ti-building-factory',color:'#805AD5',bg:'rgba(128,90,213,0.1)', label: a => `Production plan saved${a.meta?.date?` · ${a.meta.date}`:''}` },
  attendance_marked: { icon:'ti-users',           color:'#01B574', bg:'rgba(1,181,116,0.1)', label: a => `Attendance marked — ${a.meta?.count||''}` },
  crate_saved:       { icon:'ti-box',             color:'#b7791f', bg:'rgba(183,121,31,0.1)',label: a => a.meta?.noDelivery ? `No delivery — ${a.meta?.customerName||''}` : `Crate entry saved — ${a.meta?.customerName||''}` },
  task_created:      { icon:'ti-check',           color:'#E53E3E', bg:'rgba(229,62,62,0.1)', label: a => `New task — ${a.meta?.title||''}` },
  advance_added:     { icon:'ti-wallet',          color:'#b7791f', bg:'rgba(183,121,31,0.1)',label: a => `Advance recorded — ${a.meta?.staffName||''}` },
  payslip_paid:      { icon:'ti-receipt',         color:'#01B574', bg:'rgba(1,181,116,0.1)', label: a => `Payslip paid — ${a.meta?.staffName||''}` },
  reminder_done:     { icon:'ti-bell',            color:'#4318FF', bg:'rgba(67,24,255,0.1)', label: a => `Reminder done — ${a.meta?.title||''}` },
};

// Module access map for activity feed filtering
const ACTIVITY_MODULE = {
  order_new:        'orders',
  order_delivered:  'orders',
  order_deleted:    'orders',
  production_saved: 'production',
  attendance_marked:'attendance',
  crate_saved:      'crates',
  task_created:     'tasks',
  advance_added:    null,   // admin only
  payslip_paid:     null,   // admin only
  reminder_done:    'assets',
};

// Checklist check types → how to auto-verify
const CHECK_RESOLVERS = {
  attendance: (data) => {
    const rec = data.todayAttendance?.records || {};
    const markedCount = Object.values(rec).filter(r => r.status && r.status !== 'unmarked').length;
    // If we have staff count (admin), check all marked
    // If not (staff), check if any attendance was saved today
    const total = data.staffCount || 0;
    if (total > 0) return markedCount >= total;
    return markedCount > 0;
  },
  orders_delivered: (data) => {
    const todayOrds = data.orders?.filter(o => o.deliveryDate === todayStr()) || [];
    // If no orders today — consider it done (nothing to deliver)
    if (todayOrds.length === 0) return true;
    const pending = todayOrds.filter(o => o.status !== 'delivered').length;
    return pending === 0;
  },
  production_saved: (data) => !!data.productionPlan?.date,
  crates_entered: (data) => {
    // Tick if ALL enabled shops have a saved entry today
    // (includes "No delivery" saves which also set savedAt)
    const shops = data.shops?.length || 0;
    const entered = data.crateEntries || 0;
    // If no shops configured — can't tick
    if (shops === 0) return false;
    // All shops covered
    return entered >= shops;
  },
};

// ── Quick actions config ──────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { id:'new_order',   label:'New Order',        icon:'ti-plus',             module:'orders',     action:'new_order' },
  { id:'attendance',  label:'Mark Attendance',  icon:'ti-users',            module:'attendance', action:'open' },
  { id:'crate_entry', label:'Crate Entry',      icon:'ti-box',              module:'crates',     action:'open' },
  { id:'new_task',    label:'New Task',         icon:'ti-check',            module:'tasks',      action:'open' },
  { id:'production',  label:'Production Plan',  icon:'ti-building-factory', module:'production', action:'open' },
  { id:'salary',      label:'Salary',           icon:'ti-wallet',           module:'salary',     action:'open' },
];

// ════════════════════════════════════════════════════════════════════════════
// ACTIVITY FEED
// ════════════════════════════════════════════════════════════════════════════
// Feed filter categories
const FEED_FILTERS = [
  { id:'all',        label:'All' },
  { id:'orders',     label:'Orders' },
  { id:'crates',     label:'Crates' },
  { id:'attendance', label:'Attend.' },
  { id:'production', label:'Production' },
  { id:'tasks',      label:'Tasks' },
];

// Map activity type → filter id
const FEED_FILTER_MAP = {
  order_new:'orders', order_delivered:'orders', order_deleted:'orders',
  crate_saved:'crates',
  attendance_marked:'attendance',
  production_saved:'production',
  task_created:'tasks',
  advance_added:'admin', payslip_paid:'admin', reminder_done:'assets',
};

const fmtFeedDate = ts => {
  if (!ts) return '';
  const d    = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  const now  = new Date();
  const diff = Math.floor((now - d) / 60000);
  if (diff < 1)    return 'Just now';
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
  // Older than a day — show date
  const isThisYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', ...(isThisYear ? {} : { year:'numeric' }) })
    + ' · ' + d.toLocaleTimeString('en-IN', { hour:'numeric', minute:'2-digit', hour12:true });
};

function ActivityFeed({ profile, canAccess }) {
  const [all,       setAll]       = useState([]);
  const [feedErr,   setFeedErr]   = useState(false);
  const [feedFilter,setFeedFilter]= useState('all');
  const [showAll,   setShowAll]   = useState(false);
  const PAGE = 20;

  useEffect(() => {
    setFeedErr(false);
    const q = query(
      collection(db, 'activity_log'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    return onSnapshot(q,
      snap => {
        const docs = snap.docs.map(d => ({ id:d.id, ...d.data() }));
        const filtered = docs.filter(a => {
          const mod = ACTIVITY_MODULE[a.type];
          if (mod === null) return profile?.role === 'admin';
          if (!mod) return true;
          return canAccess(mod);
        });
        setAll(filtered);
        setFeedErr(false);
      },
      _err => {
        // Query failed — show error state instead of silent empty
        setFeedErr(true);
      }
    );
  }, [profile?.role]);

  // Apply filter chip
  const visible = feedFilter === 'all'
    ? all
    : all.filter(a => FEED_FILTER_MAP[a.type] === feedFilter);

  const shown   = showAll ? visible : visible.slice(0, PAGE);
  const hasMore = !showAll && visible.length > PAGE;

  if (feedErr) return (
    <div style={{ padding:'12px 0', fontSize:'calc(var(--fs) - 2px)',
      color:'var(--lyt-red)', textAlign:'center' }}>
      Feed unavailable — check connection
    </div>
  );

  return (
    <div>
      {/* Filter chips */}
      <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 }}>
        {FEED_FILTERS.map(f => (
          <button key={f.id} onClick={() => { setFeedFilter(f.id); setShowAll(false); }}
            style={{
              padding:'3px 10px', borderRadius:20,
              fontSize:'calc(var(--fs) - 2px)', fontWeight:600,
              cursor:'pointer', fontFamily:'Inter', border:'none',
              background: feedFilter===f.id ? 'var(--lyt-accent)' : 'rgba(67,24,255,0.06)',
              color: feedFilter===f.id ? '#fff' : 'var(--lyt-t3)',
              transition:'all 0.12s',
            }}>{f.label}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div style={{ padding:'16px 0', fontSize:'calc(var(--fs) - 1px)',
          color:'var(--lyt-t4)', textAlign:'center' }}>
          {all.length === 0 ? 'No activity yet' : `No ${feedFilter} activity`}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
          {shown.map((a, idx) => {
            const cfg = ACTIVITY_CONFIG[a.type];
            if (!cfg) return null;
            return (
              <div key={a.id} style={{
                display:'flex', gap:10, padding:'9px 0',
                borderBottom: idx < shown.length-1 ? '0.5px solid var(--lyt-border)' : 'none',
                alignItems:'flex-start',
              }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                  <div style={{ width:28, height:28, borderRadius:7, background:cfg.bg,
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <i className={`ti ${cfg.icon}`}
                      style={{ fontSize:'calc(var(--fs) - 2px)', color:cfg.color }}/>
                  </div>
                  {idx < shown.length-1 && (
                    <div style={{ width:1, flex:1, minHeight:6, background:'var(--lyt-border)', marginTop:3 }}/>
                  )}
                </div>
                <div style={{ flex:1, minWidth:0, paddingTop:1 }}>
                  <div style={{ fontSize:'var(--fs)', fontWeight:500, color:'var(--lyt-t1)',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {cfg.label(a)}
                  </div>
                  <div style={{ fontSize:'calc(var(--fs) - 2px)', color:'var(--lyt-t4)', marginTop:2,
                    display:'flex', gap:5, alignItems:'center', flexWrap:'wrap' }}>
                    {a.actorName && <span style={{ fontWeight:500 }}>{a.actorName}</span>}
                    {a.actorName && <span style={{ opacity:0.4 }}>·</span>}
                    <span>{fmtFeedDate(a.timestamp)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <button onClick={() => setShowAll(true)}
          style={{ marginTop:10, width:'100%', padding:'7px', borderRadius:8,
            border:'0.5px solid var(--lyt-border)', background:'transparent',
            fontSize:'calc(var(--fs) - 2px)', fontWeight:600, color:'var(--lyt-t3)',
            cursor:'pointer', fontFamily:'Inter' }}>
          Load more ({visible.length - PAGE} more)
        </button>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DAILY CHECKLIST
// ════════════════════════════════════════════════════════════════════════════
function DailyChecklist({ profile, canAccess, data, onModule }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    getDoc(doc(db, 'app_config', 'home_checklist')).then(snap => {
      if (snap.exists()) {
        setItems(snap.data().items || []);
      } else {
        // Default checklist items
        const defaults = [
          { id:'att',  label:'Mark Attendance',     module:'attendance', checkType:'attendance',       icon:'ti-users' },
          { id:'ord',  label:'Deliver all orders',  module:'orders',     checkType:'orders_delivered', icon:'ti-truck' },
          { id:'crt',  label:'Save crate entries',  module:'crates',     checkType:'crates_entered',   icon:'ti-box' },
          { id:'prd',  label:'Set production plan', module:'production', checkType:'production_saved',  icon:'ti-building-factory' },
        ];
        setItems(defaults);
      }
    });
  }, []);

  // Filter to modules the user can access
  const visible = items.filter(item => !item.module || canAccess(item.module));

  if (visible.length === 0) return null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
      {visible.map((item, idx) => {
        const resolver = CHECK_RESOLVERS[item.checkType];
        const done = resolver ? resolver(data) : false;
        return (
          <div key={item.id} onClick={() => item.module && onModule(item.module)}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'9px 0', cursor: item.module ? 'pointer' : 'default',
              borderBottom: idx < visible.length-1 ? '0.5px solid var(--lyt-border)' : 'none',
            }}>
            {/* Check indicator */}
            <div style={{
              width:20, height:20, borderRadius:6, flexShrink:0,
              border:`1.5px solid ${done ? '#01B574' : 'var(--lyt-border)'}`,
              background: done ? 'rgba(1,181,116,0.12)' : 'transparent',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              {done && <i className="ti ti-check"
                style={{ fontSize:'calc(var(--fs) - 3px)', color:'#01B574' }}/>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontSize:'var(--fs)', fontWeight:500,
                color: done ? 'var(--lyt-t4)' : 'var(--lyt-t1)',
                textDecoration: done ? 'line-through' : 'none',
              }}>{item.label}</div>
            </div>
            {!done && item.module && (
              <i className="ti ti-chevron-right"
                style={{ fontSize:'calc(var(--fs) - 2px)', color:'var(--lyt-t4)', flexShrink:0 }}/>
            )}
            {done && (
              <span style={{ fontSize:'calc(var(--fs) - 3px)', color:'#01B574',
                fontWeight:600, flexShrink:0 }}>Done</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ATTENTION STRIP — tasks due soon + asset reminders combined
// ════════════════════════════════════════════════════════════════════════════
function RemindersStrip({ user, profile, onModule }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const today = todayStr();
    const in7   = toISO(addDays(new Date(), 7));
    const in30  = toISO(addDays(new Date(), 30));
    const isAdmin = profile?.role === 'admin';

    const p1 = () => getDocs(collection(db, 'asset_reminders')).then(snap =>
      snap.docs.map(d => ({ id:'ar_'+d.id, ...d.data(), _type:'asset' }))
        .filter(r => {
          if (r.done || !r.dueDate || r.dueDate > in30) return false;
          if (isAdmin) return true;
          return r.assignees?.some(a => a.uid === user?.uid);
        })
    ).catch(() => []);

    const p2 = getDocs(collection(db, 'tasks')).then(snap =>
      snap.docs.map(d => ({ id:'t_'+d.id, ...d.data(), _type:'task' }))
        .filter(t => {
          if (t.done || !t.dueDate || t.dueDate > in7) return false;
          if (isAdmin) return true;
          // Visible to user
          return !t.visibleTo || t.visibleTo === 'all' ||
            (Array.isArray(t.visibleTo) && t.visibleTo.includes(user?.uid));
        })
    ).catch(() => []);

    Promise.all([p1(), p2]).then(([assetItems, taskItems]) => {
      const all = [...assetItems, ...taskItems]
        .sort((a, b) => (a.dueDate||'').localeCompare(b.dueDate||''))
        .slice(0, 6);
      setItems(all);
    });
  }, [user?.uid, profile?.role]);

  if (items.length === 0) return null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {items.map(item => {
        const today   = todayStr();
        const dueDate = item.dueDate || '';
        const daysOut = dueDate ? Math.ceil((new Date(dueDate+'T00:00:00') - new Date()) / 86400000) : 99;
        const overdue = dueDate && dueDate < today;
        const soon    = !overdue && daysOut <= 2;
        const accentColor = overdue ? 'var(--lyt-red)' : soon ? '#b7791f' : 'var(--lyt-accent)';
        const bgColor     = overdue ? 'rgba(229,62,62,0.08)' : soon ? 'rgba(183,121,31,0.08)' : 'rgba(67,24,255,0.08)';
        const dueLabel    = overdue ? `Overdue by ${Math.abs(daysOut)}d`
          : daysOut === 0 ? 'Due today'
          : daysOut === 1 ? 'Due tomorrow'
          : `${daysOut}d left`;
        const icon  = item._type === 'task' ? 'ti-check' : 'ti-bell';
        const label = item._type === 'task'
          ? (item.title || 'Task')
          : (item.title || item.reminderTitle || 'Reminder');
        const sub   = item._type === 'task'
          ? (item.tags?.length ? item.tags.join(', ') : null)
          : (item.assetName || null);
        const dest  = item._type === 'task' ? 'tasks' : 'assets';
        const typeLabel = item._type === 'task' ? 'Task' : 'Reminder';

        return (
          <div key={item.id} className="lyt-ac"
            onClick={() => onModule(dest)}
            style={{ borderLeftColor: accentColor, cursor:'pointer' }}>
            <div className="lyt-ac-top">
              {/* Icon box */}
              <div style={{ width:36, height:36, borderRadius:9, flexShrink:0,
                background: bgColor,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className={`ti ${icon}`}
                  style={{ fontSize:'calc(var(--fs) + 2px)', color: accentColor }}/>
              </div>
              {/* Content */}
              <div style={{ flex:1, minWidth:0 }}>
                <div className="lyt-ac-nm">{label}</div>
                <div style={{ fontSize:'calc(var(--fs) - 2px)', color:'var(--lyt-t4)',
                  marginTop:2, display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                  <span style={{ padding:'1px 7px', borderRadius:20,
                    fontSize:'calc(var(--fs) - 4px)', fontWeight:700,
                    background: bgColor, color: accentColor,
                    border:`1px solid ${accentColor}33` }}>
                    {dueLabel}
                  </span>
                  {sub && <span>{sub}</span>}
                  <span style={{ color:'var(--lyt-border)' }}>·</span>
                  <span>{typeLabel}</span>
                </div>
              </div>
              {/* Chevron */}
              <i className="ti ti-chevron-right"
                style={{ fontSize:'calc(var(--fs) - 1px)', color:'var(--lyt-t4)', flexShrink:0 }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MINIMAL HOME (2-module users)
// ════════════════════════════════════════════════════════════════════════════
function MinimalHome({ profile, modules, onModule }) {
  const h = new Date().getHours();
  const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';

  const MODULE_META = {
    orders:     { label:'Orders',        icon:'ti-clipboard-list',  color:'#4318FF', bg:'rgba(67,24,255,0.08)' },
    attendance: { label:'Attendance',    icon:'ti-users',           color:'#01B574', bg:'rgba(1,181,116,0.08)' },
    production: { label:'Production',    icon:'ti-building-factory',color:'#805AD5', bg:'rgba(128,90,213,0.08)'},
    crates:     { label:'Crate Tracker', icon:'ti-box',             color:'#b7791f', bg:'rgba(183,121,31,0.08)'},
    tasks:      { label:'Tasks & Notes', icon:'ti-check',           color:'#E53E3E', bg:'rgba(229,62,62,0.08)' },
    salary:     { label:'Salary',        icon:'ti-wallet',          color:'#b7791f', bg:'rgba(183,121,31,0.08)'},
    assets:     { label:'Assets & Ops',  icon:'ti-tool',            color:'#4318FF', bg:'rgba(67,24,255,0.08)' },
    recipes:    { label:'Recipes',       icon:'ti-chef-hat',        color:'#805AD5', bg:'rgba(128,90,213,0.08)'},
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', minHeight:'50vh', gap:24, padding:16 }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:'calc(var(--fs) + 8px)', fontWeight:700,
          color:'var(--lyt-t1)', marginBottom:4 }}>
          {greeting}, {profile?.name?.split(' ')[0] || 'there'}
        </div>
        <div style={{ fontSize:'var(--fs)', color:'var(--lyt-t4)' }}>
          {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${modules.length},1fr)`,
        gap:14, width:'100%', maxWidth:400 }}>
        {modules.map(mod => {
          const meta = MODULE_META[mod] || { label:mod, icon:'ti-grid', color:'var(--lyt-accent)', bg:'rgba(67,24,255,0.08)' };
          return (
            <div key={mod} onClick={() => onModule(mod)}
              style={{ background:meta.bg, border:`1.5px solid ${meta.color}33`,
                borderRadius:16, padding:'28px 16px', textAlign:'center',
                cursor:'pointer', transition:'all 0.15s' }}>
              <div style={{ width:56, height:56, borderRadius:14, background:meta.color,
                display:'flex', alignItems:'center', justifyContent:'center',
                margin:'0 auto 12px' }}>
                <i className={`ti ${meta.icon}`}
                  style={{ fontSize:'calc(var(--fs) + 10px)', color:'#fff' }}/>
              </div>
              <div style={{ fontSize:'calc(var(--fs) + 2px)', fontWeight:700,
                color:meta.color }}>{meta.label}</div>
              <div style={{ fontSize:'calc(var(--fs) - 2px)', color:'var(--lyt-t4)',
                marginTop:4 }}>Tap to open</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN HOME
// ════════════════════════════════════════════════════════════════════════════
export default function Home({ onModule, onOpenNotes }) {
  const { user, profile } = useAppContext();
  const today = todayStr();
  const isAdmin = profile?.role === 'admin';
  const [dataLoading, setDataLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Reload data when window regains focus — checklist stays fresh
  const [loadTick, setLoadTick] = useState(0);
  useEffect(() => {
    const onFocus = () => setLoadTick(t => t + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const canAccess = mod => {
    if (!mod) return true;
    if (isAdmin) return true;
    if (['admin','salary'].includes(mod)) return false;
    if (mod === 'assets') return profile?.modules?.includes('assets') || profile?.modules?.includes('assets_log');
    const allowed = profile?.modules;
    if (!allowed) return true;
    return allowed.includes(mod);
  };

  // Accessible modules list (excluding admin)
  const accessibleModules = ['orders','attendance','production','crates','tasks',
    'salary','assets','recipes'].filter(canAccess);

  // ── Auto-redirect: 1 module → go directly ────────────────────────────────
  const redirected = useRef(false);
  useEffect(() => {
    if (redirected.current) return;
    if (profile && !isAdmin && accessibleModules.length === 1) {
      redirected.current = true;
      onModule(accessibleModules[0]);
    }
  }, [profile]);

  // ── Data loading ──────────────────────────────────────────────────────────
  const [stats,           setStats]           = useState({ orders:0, ordersDone:0, overdueOrders:0, attendance:'—', staffTotal:0, staffPresent:0, tasksDue:0, cratesOut:0, cratesToday:null });
  const [checklistData,   setChecklistData]   = useState({});
  const [quickActions,    setQuickActions]    = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const promises = [];
        if (canAccess('orders'))     promises.push(getDocs(collection(db,'orders')));         else promises.push(Promise.resolve(null));
        if (canAccess('attendance')) promises.push(getDoc(doc(db,'attendance',today)));        else promises.push(Promise.resolve(null));
        if (isAdmin)                 promises.push(getDocs(collection(db,'users')));          else promises.push(Promise.resolve(null));
        if (canAccess('tasks'))      promises.push(getDocs(collection(db,'tasks')));          else promises.push(Promise.resolve(null));
        if (canAccess('production')) promises.push(getDoc(doc(db,'production_plans',today))); else promises.push(Promise.resolve(null));
        if (canAccess('crates'))     promises.push(getDocs(collection(db,'crate_entries')));  else promises.push(Promise.resolve(null));
        if (canAccess('crates'))     promises.push(getDocs(collection(db,'crate_config')));    else promises.push(Promise.resolve(null));

        const [ordSnap, attSnap, usersSnap, taskSnap, prodSnap, crateSnap, crateConfigSnap] = await Promise.all(promises);

        const normDate = v => {
          if (!v) return '';
          if (typeof v === 'string') return v.slice(0,10);
          if (v.toDate) return toISO(v.toDate());
          if (v.seconds) return toISO(new Date(v.seconds*1000));
          return '';
        };

        // Orders
        let todayOrders = 0, todayDone = 0, overdueOrders = 0;
        const allOrders = [];
        if (ordSnap) {
          ordSnap.docs.forEach(d => {
            const o = { id:d.id, ...d.data(), deliveryDate: normDate(d.data().deliveryDate) };
            allOrders.push(o);
            if (o.deliveryDate === today) {
              todayOrders++;
              if (o.status === 'delivered') todayDone++;
            }
            // Overdue — past delivery date and not delivered
            if (o.status !== 'delivered' && o.deliveryDate && o.deliveryDate < today) {
              overdueOrders++;
            }
          });
        }

        // Attendance
        let staffTotal = 0, staffPresent = 0, todayAttData = {};
        const staffIds = new Set();
        if (usersSnap) {
          // Mirror AppContext's user rules EXACTLY so Home's count always matches
          // the attendance module's staff list:
          //  1. hide superseded (_migratedTo) docs when their replacement exists
          //  2. exclude admins, attendance-inactive, and deactivated staff
          const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const allIds = new Set(allUsers.map(u => u.id));
          allUsers
            .filter(u => !(u._migratedTo && allIds.has(u._migratedTo)))
            .filter(u => u.role !== 'admin' && u.attendanceActive !== false && !u.inactive)
            .forEach(u => staffIds.add(u.id));
          staffTotal = staffIds.size;
        }
        if (attSnap) {
          todayAttData = attSnap.exists?.() ? attSnap.data() : {};
          const rec = todayAttData.records || {};
          // For admin: count against known staff IDs
          // For non-admin: count all marked records
          if (staffIds.size > 0) {
            staffPresent = Object.entries(rec)
              .filter(([id,r]) => staffIds.has(id) && (r.status==='present'||r.status==='late')).length;
          } else {
            staffPresent = Object.values(rec)
              .filter(r => r.status==='present'||r.status==='late').length;
            // Use records count as staffTotal fallback for non-admin
            if (staffTotal === 0) staffTotal = Object.keys(rec).length;
          }
        }

        // Tasks
        let tasksDue = 0;
        if (taskSnap) {
          tasksDue = taskSnap.docs.filter(d => {
            const t = d.data();
            if (t.done) return false;
            const visible = isAdmin || !t.visibleTo || t.visibleTo==='all' ||
              (Array.isArray(t.visibleTo) && t.visibleTo.includes(user?.uid));
            return visible && t.dueDate && t.dueDate <= today;
          }).length;
        }

        // Production plan check
        const productionSaved = prodSnap?.exists?.() ? prodSnap.data() : null;

        // Crates outstanding — mirrors CratesModule logic exactly
        let cratesOut = 0;
        if (crateSnap && crateConfigSnap) {
          // Build per-customer config map (doc ID = customer ID)
          const cfgMap = {};
          crateConfigSnap.docs.forEach(d => { cfgMap[d.id] = d.data(); });
          // Sum all crate entry records across all time
          const pendingMap = {};
          crateSnap.docs.forEach(d => {
            Object.entries(d.data().records || {}).forEach(([cid, rec]) => {
              pendingMap[cid] = (pendingMap[cid] || 0) + (rec.delivered||0) - (rec.returned||0);
            });
          });
          // getPending(custId) = initialQty + pendingMap[custId]
          // Only count enabled customers, only positive values
          Object.entries(cfgMap).forEach(([cid, cfg]) => {
            if (!cfg.enabled) return;
            const pending = (parseFloat(cfg.initialQty)||0) + (pendingMap[cid]||0);
            if (pending > 0) cratesOut += pending;
          });
        }

        // Crate today summary — delivered and returned today
        // (crateSnap is the full collection, still needed for cratesOut pendingMap above;
        //  find today's doc once and reuse for both summary and checklist count)
        const crateTodayDoc = crateSnap ? crateSnap.docs.find(d => d.id === today) : null;
        let crateDelToday = 0, crateRetToday = 0;
        if (crateTodayDoc) {
          Object.values(crateTodayDoc.data().records || {}).forEach(rec => {
            crateDelToday += rec.delivered || 0;
            crateRetToday += rec.returned  || 0;
          });
        }

        setStats({ orders:todayOrders, ordersDone:todayDone, overdueOrders, attendance:`${staffPresent}/${staffTotal}`,
          staffTotal, staffPresent, tasksDue, cratesOut,
          cratesToday: (crateDelToday > 0 || crateRetToday > 0)
            ? { del:crateDelToday, ret:crateRetToday } : null });

        // Checklist data
        const shops = crateConfigSnap?.docs ? crateConfigSnap.docs.filter(d => d.data().enabled).map(d => ({id:d.id,...d.data()})) : [];
        setChecklistData({
          staffCount: staffTotal,
          todayAttendance: todayAttData,
          orders: allOrders,
          productionPlan: productionSaved,
          shops,
          // Count how many shops have a saved entry today (reuse today's doc found above)
          crateEntries: crateTodayDoc
            ? Object.values(crateTodayDoc.data().records || {}).filter(r => r.savedAt).length
            : 0,
        });

        // Quick actions — sorted by urgency
        const urgencyScore = a => {
          if (a.id === 'attendance' && staffPresent < staffTotal) return 10;
          if (a.id === 'new_order'  && todayOrders - todayDone > 0) return 9;
          if (a.id === 'crate_entry' && cratesOut > 0) return 8;
          if (a.id === 'production' && !productionSaved) return 7;
          return 0;
        };
        const actions = QUICK_ACTIONS
          .filter(a => canAccess(a.module))
          .sort((a, b) => urgencyScore(b) - urgencyScore(a))
          .slice(0, isAdmin ? 6 : 4);
        setQuickActions(actions);

      } catch { /* silent — UI shows empty state */ }
      setDataLoading(false);
    };
    if (profile) load();
  }, [user?.uid, profile?.role, loadTick]);

  // ── 2-module view ─────────────────────────────────────────────────────────
  if (!isAdmin && accessibleModules.length <= 2 && accessibleModules.length > 0) {
    return <MinimalHome profile={profile} modules={accessibleModules} onModule={onModule}/>;
  }

  // ── Full home ─────────────────────────────────────────────────────────────
  if (!profile || dataLoading) return <ModuleLoader/>;
  const h = new Date().getHours();
  const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';

  const STAT_CARDS = [
    canAccess('orders') && {
      label:"Today's Orders", icon:'ti-clipboard-list',
      value:`${stats.ordersDone}/${stats.orders}`,
      sub: stats.overdueOrders > 0
        ? `${stats.overdueOrders} overdue${stats.orders > 0 ? ` · ${stats.orders - stats.ordersDone} pending` : ''}`
        : stats.orders > 0 ? `${stats.orders - stats.ordersDone} pending` : 'No orders today',
      bg:'linear-gradient(135deg,#4318FF,#6AD2FF)', mod:'orders',
      alert: stats.overdueOrders > 0 || (stats.orders > 0 && stats.ordersDone < stats.orders),
    },
    canAccess('attendance') && {
      label:'Attendance', icon:'ti-users',
      value: stats.attendance,
      sub: stats.staffPresent < stats.staffTotal ? `${stats.staffTotal - stats.staffPresent} absent/unmarked` : 'All marked',
      bg:'linear-gradient(135deg,#01B574,#68d391)', mod:'attendance',
    },
    canAccess('crates') && {
      label:'Crates Outstanding', icon:'ti-box',
      value: stats.cratesOut || '—',
      sub: stats.cratesToday
        ? `${stats.cratesToday.del} out · ${stats.cratesToday.ret} in today`
        : 'Pending returns',
      bg:'linear-gradient(135deg,#F6AD55,#fbd38d)', mod:'crates',
      iconColor:'#7B4F12',
    },
    canAccess('tasks') && {
      label:'Tasks Due', icon:'ti-check',
      value: stats.tasksDue,
      sub: stats.tasksDue > 0 ? 'Needs attention' : 'All clear',
      bg: stats.tasksDue > 0 ? 'linear-gradient(135deg,#E53E3E,#FC8181)' : 'linear-gradient(135deg,#805AD5,#b794f4)',
      mod:'tasks', alert: stats.tasksDue > 0,
    },
  ].filter(Boolean);

  const handleQuickAction = (action) => {
    if (action.action === 'new_order') {
      onModule('orders');
      // The + New Order button is in the Orders module itself
    } else {
      onModule(action.module);
    }
  };

  return (
    <div>
      {/* ── Greeting ── */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:'calc(var(--fs) + 8px)', fontWeight:700,
          color:'var(--lyt-t1)', letterSpacing:-0.3, marginBottom:3 }}>
          {greeting}, {profile?.name?.split(' ')[0] || 'there'}
        </div>
        <div style={{ fontSize:'var(--fs)', color:'var(--lyt-t4)' }}>
          {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          {' · '}Here's what's happening today
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="lyt-stats" style={{ marginBottom:18 }}>
        {STAT_CARDS.map((s,i) => (
          <div key={i} className="lyt-stat"
            style={s.alert ? { borderColor:'rgba(229,62,62,0.35)' } : {}}
            onClick={() => s.mod && onModule(s.mod)}>
            <div>
              <div className="lyt-stat-lbl">{s.label}</div>
              <div className="lyt-stat-val"
                style={s.alert ? { color:'var(--lyt-red)' } : {}}>{s.value}</div>
              <div style={{ fontSize:'calc(var(--fs) - 3px)', color:'var(--lyt-t4)',
                marginTop:2 }}>{s.sub}</div>
            </div>
            <div className="lyt-ib" style={{ background:s.bg }}>
              <i className={`ti ${s.icon}`}
                style={s.iconColor ? { color:s.iconColor } : {}}/>
            </div>
          </div>
        ))}
      </div>

      {/* ── Quick actions ── */}
      {quickActions.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <div className="lyt-sec-label" style={{ marginBottom:8 }}>Quick Actions</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {quickActions.map(a => (
              <button key={a.id} onClick={() => handleQuickAction(a)} style={{
                display:'flex', alignItems:'center', gap:6,
                padding:'8px 14px', borderRadius:8,
                border:'0.5px solid var(--lyt-border)',
                background:'#fff', cursor:'pointer', fontFamily:'Inter',
                fontSize:'var(--fs)', fontWeight:500, color:'var(--lyt-t1)',
                transition:'all 0.12s',
              }}>
                <i className={`ti ${a.icon}`}
                  style={{ fontSize:'calc(var(--fs) + 1px)', color:'var(--lyt-accent)' }}/>
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Main content grid ── */}
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:14, marginBottom:14 }}>
        {/* Daily checklist */}
        <div className="lyt-card" style={{ marginBottom:0 }}>
          <div className="lyt-card-head">
            <div className="lyt-card-title">
              <i className="ti ti-checklist"
                style={{ marginRight:6, fontSize:'calc(var(--fs) + 1px)', color:'var(--lyt-accent)' }}/>
              Daily Checklist
            </div>
          </div>
          <div style={{ padding:'4px 16px 12px' }}>
            <DailyChecklist
              profile={profile} canAccess={canAccess}
              data={checklistData} onModule={onModule}/>
          </div>
        </div>

        {/* Live activity feed */}
        <div className="lyt-card" style={{ marginBottom:0 }}>
          <div className="lyt-card-head">
            <div className="lyt-card-title">
              <i className="ti ti-activity"
                style={{ marginRight:6, fontSize:'calc(var(--fs) + 1px)', color:'var(--lyt-accent)' }}/>
              Live Activity
            </div>
          </div>
          <div style={{ padding:'4px 16px 12px', maxHeight:280, overflowY:'auto' }}>
            <ActivityFeed profile={profile} canAccess={canAccess}/>
          </div>
        </div>
      </div>

      {/* ── Asset reminders ── */}
      {(canAccess('assets') || canAccess('tasks')) && (
        <div style={{ marginBottom:14 }}>
          <div className="lyt-sec-label" style={{ marginBottom:8 }}>
            <i className="ti ti-alert-circle"
              style={{ marginRight:4, fontSize:'calc(var(--fs) - 1px)' }}/>
            Needs Attention
          </div>
          <RemindersStrip user={user} profile={profile} onModule={onModule}/>
        </div>
      )}
    </div>
  );
}
